import { assertNever } from "@/app/(application)/(ttv)/ttv/analytics/assertNever";
import type {
  RecordedTtvAnalyticsEvent,
  TtvControlValue,
  TtvControlValueType,
} from "@/app/(application)/(ttv)/ttv/analytics/types";

// A control's before/after values land on a property pair chosen by their TYPE. PostHog fixes a property's type at first ingestion and branches its filters and binning on it, so one shared pair carrying tempo numbers, "4/4" strings and metronome booleans meant whichever shape arrived first decided whether the others were usable at all — and silently removed numeric filtering from bpm for every producer of that key. The type is decided by whichever side is non-null (a control moving to or from "no value" keeps its own shape), and only the matching pair is emitted, so no event ever carries an empty key of the wrong shape.
const toControlValueProperties = (
  previousValue: TtvControlValue,
  newValue: TtvControlValue
) => {
  const shapeOf = (value: TtvControlValue): TtvControlValueType | null => {
    if (typeof value === "number") {
      return "number";
    }
    if (typeof value === "string") {
      return "text";
    }
    if (typeof value === "boolean") {
      return "flag";
    }
    return null;
  };
  const valueType: TtvControlValueType =
    shapeOf(newValue) ?? shapeOf(previousValue) ?? "text";
  if (valueType === "number") {
    return {
      value_type: valueType,
      previous_value_number: previousValue as number | null,
      new_value_number: newValue as number | null,
    };
  }
  if (valueType === "flag") {
    return {
      value_type: valueType,
      previous_value_flag: previousValue as boolean | null,
      new_value_flag: newValue as boolean | null,
    };
  }
  return {
    value_type: valueType,
    previous_value_text: previousValue as string | null,
    new_value_text: newValue as string | null,
  };
};

// The domain shape is camelCase (it is TypeScript); the wire is snake_case, matching ttv_stability. Rebuilt per variant rather than key-mapped generically, so a renamed domain field cannot silently change a PostHog property name that dashboards are built on.
export const toTtvEventPostHogProperties = (
  record: RecordedTtvAnalyticsEvent
) => {
  const common = {
    schema_version: record.schemaVersion,
    recorded_at: record.recordedAt,
  };

  switch (record.event) {
    case "ttv_convert_shown":
      return {
        ...common,
        project_id: record.projectId,
      };
    case "ttv_convert_clicked":
      return {
        ...common,
        project_id: record.projectId,
      };
    case "ttv_convert_requested":
      return {
        ...common,
        project_id: record.projectId,
        singer_id: record.singerId,
        ttv_transfer_id: record.transferId,
        take_count: record.takeCount,
      };
    case "ttv_convert_failed":
      return {
        ...common,
        project_id: record.projectId,
        singer_id: record.singerId,
        ttv_transfer_id: record.transferId,
        transfer_stage: record.stage,
      };
    case "ttv_convert_transferred":
      return {
        ...common,
        project_id: record.projectId,
        singer_id: record.singerId,
        ttv_transfer_id: record.transferId,
        sound_ids: record.takes.map((take) => take.soundId).join(","),
        take_indexes: record.takes.map((take) => take.takeIndex).join(","),
        take_count: record.takes.length,
        vocal_duration_seconds: record.vocalDurationSeconds,
        transfer_ms: record.transferMs,
        transfer_save_ms: record.saveMs,
        transfer_render_ms: record.renderMs,
        transfer_upload_ms: record.uploadMs,
      };
    case "ttv_convert_input_ready":
      return {
        ...common,
        sound_id: record.soundId,
        ttv_transfer_id: record.transferId,
        $insert_id: `ttv-input-${record.soundId}`,
      };
    case "conversion_completed":
      return {
        ...common,
        sound_id: record.soundId,
        conversion_id: record.conversionId,
        ttv_transfer_id: record.transferId,
        $insert_id: `conversion-completed-${record.conversionId}`,
      };

    case "ttv_session_started":
      return {
        ...common,
        days_since_last_session: record.daysSinceLastSession,
        project_count: record.projectCount,
        has_previous_ttv_work: record.hasPreviousTtvWork,
      };
    case "ttv_project_created":
      return {
        ...common,
        creation_source: record.creationSource,
        project_count_before: record.projectCountBefore,
        ...(record.isFirstTtvProject === undefined ?
          {}
        : { is_first_ttv_project: record.isFirstTtvProject }),
      };
    case "ttv_project_reopened":
      return {
        ...common,
        reopen_source: record.reopenSource,
        project_age_days: record.projectAgeDays,
        days_since_last_opened: record.daysSinceLastOpened,
        project_note_count: record.projectNoteCount,
        roster_singer_count: record.rosterSingerCount,
        has_backing_track: record.hasBackingTrack,
        bpm: record.bpm,
      };
    case "ttv_singer_picker_opened":
      return {
        ...common,
        opened_from: record.openedFrom,
        roster_singer_count: record.rosterSingerCount,
      };
    case "ttv_singer_selected":
      return {
        ...common,
        voice_id: record.voiceId,
        replaced_voice_id: record.replacedVoiceId,
        replaced_singer_slot: record.replacedSingerSlot,
        discovery_method: record.discoveryMethod,
        sort_variant: record.sortVariant,
        visible_list_position: record.visibleListPosition,
        was_default_singer_replaced: record.wasDefaultSingerReplaced,
      };
    case "ttv_singer_added":
      return {
        ...common,
        voice_id: record.voiceId,
        singer_slot: record.singerSlot,
        roster_singer_count: record.rosterSingerCount,
        was_part_copied: record.wasPartCopied,
      };
    case "ttv_singer_removed":
      return {
        ...common,
        voice_id: record.voiceId,
        singer_slot: record.singerSlot,
        roster_singer_count_after: record.rosterSingerCountAfter,
      };
    case "ttv_singer_focus_switched":
      return {
        ...common,
        previous_voice_id: record.previousVoiceId,
        previous_singer_slot: record.previousSingerSlot,
        new_voice_id: record.newVoiceId,
        new_singer_slot: record.newSingerSlot,
        roster_singer_count: record.rosterSingerCount,
      };
    case "ttv_notes_entered":
      return {
        ...common,
        input_method: record.inputMethod,
        entered_note_count: record.enteredNoteCount,
      };
    case "ttv_backing_track_added":
      // The wire keys are unchanged — dashboards keep working. The success flag is derived from the variant here rather than stored alongside it, so it can never disagree with the payload.
      if (record.bpmDetection === "succeeded") {
        return {
          ...common,
          bpm_detection_succeeded: true,
          detected_bpm: record.detectedBpm,
          bpm_source: record.bpmSource,
          duration_seconds: record.durationSeconds,
        };
      }
      return {
        ...common,
        bpm_detection_succeeded: false,
        detected_bpm: null,
        bpm_source: null,
        duration_seconds: record.durationSeconds,
      };
    case "ttv_generate_requested":
      return {
        ...common,
        intent_source: record.intentSource,
        resing_scope: record.resingScope,
        resing_note_count: record.resingNoteCount,
        variance_model_id: record.varianceModelId,
        variance_steps: record.varianceSteps,
        acoustic_steps: record.acousticSteps,
        roster_voice_ids: record.rosterVoiceIds,
        run_singer_count: record.runSingerCount,
        roster_singer_count: record.rosterSingerCount,
        unison_layers: record.unisonLayers,
        backing_track_present: record.backingTrackPresent,
        project_note_count: record.projectNoteCount,
        run_note_count: record.runNoteCount,
        has_pitch_edits_this_session: record.hasPitchEditsThisSession,
        notes_manually_edited_this_session:
          record.notesManuallyEditedThisSession,
        mean_vibrato: record.meanVibrato,
        mean_de_vibrato: record.meanDeVibrato,
        mean_vibrato_rate: record.meanVibratoRate,
        harmony_preset: record.harmonyPreset,
        effects_enabled: record.effectsEnabled,
        effects_preset: record.effectsPreset,
        effects_brightness: record.effectsBrightness,
        effects_compression: record.effectsCompression,
        effects_de_esser: record.effectsDeEsser,
        effects_saturation: record.effectsSaturation,
        effects_delay: record.effectsDelay,
        effects_reverb: record.effectsReverb,
        effects_compression_mode: record.effectsCompressionMode,
        effects_delay_note_value: record.effectsDelayNoteValue,
        effects_delay_style: record.effectsDelayStyle,
        effects_reverb_mode: record.effectsReverbMode,
        effects_saturation_mode: record.effectsSaturationMode,
      };
    case "ttv_generate_succeeded":
      return {
        ...common,
        intent_source: record.intentSource,
        duration_ms: record.durationMs,
        rendered_audio_seconds: record.renderedAudioSeconds,
        run_singer_count: record.runSingerCount,
        leg_count: record.legCount,
        run_voice_ids: record.runVoiceIds,
      };
    case "ttv_generate_failed":
      return {
        ...common,
        intent_source: record.intentSource,
        duration_ms: record.durationMs,
        error_type: record.errorType,
        run_singer_count: record.runSingerCount,
        leg_count: record.legCount,
        run_voice_ids: record.runVoiceIds,
      };
    case "ttv_generate_cancelled":
      return {
        ...common,
        intent_source: record.intentSource,
        duration_ms: record.durationMs,
        run_singer_count: record.runSingerCount,
        leg_count: record.legCount,
      };
    case "ttv_unison_stack_built":
      return {
        ...common,
        previous_take_count: record.previousTakeCount,
        requested_take_count: record.requestedTakeCount,
        rendered_take_count: record.renderedTakeCount,
        duration_ms: record.durationMs,
        stack_outcome: record.stackOutcome,
      };
    case "ttv_audio_render_failed":
      return {
        ...common,
        run_kind: record.runKind,
        error_type: record.errorType,
        duration_ms: record.durationMs,
      };
    case "ttv_playback_session":
      return {
        ...common,
        start_position_seconds: record.startPositionSeconds,
        stop_position_seconds: record.stopPositionSeconds,
        listened_seconds: record.listenedSeconds,
        playable_duration_seconds: record.playableDurationSeconds,
        completion_ratio: record.completionRatio,
        reached_end: record.reachedEnd,
        seek_count: record.seekCount,
        had_backing_track: record.hadBackingTrack,
        is_first_listen_of_this_render: record.isFirstListenOfThisRender,
      };
    case "ttv_control_changed":
      return {
        ...common,
        control_name: record.controlName,
        ...toControlValueProperties(record.previousValue, record.newValue),
        is_first_change_this_session: record.isFirstChangeThisSession,
      };
    case "ttv_preset_applied":
      if (record.presetType === "effects") {
        return {
          ...common,
          preset_type: record.presetType,
          preset_name: record.presetName,
          previous_preset: record.previousPreset,
        };
      }
      return {
        ...common,
        preset_type: record.presetType,
        preset_name: record.presetName,
        previous_preset: record.previousPreset,
        layer_count: record.layerCount,
        launched_from: record.launchedFrom,
      };
    case "ttv_note_edited":
      return {
        ...common,
        edit_type: record.editType,
        edited_note_count: record.editedNoteCount,
      };
    case "ttv_phoneme_timing_dragged":
      return {
        ...common,
        // Flattened: the pair is null together, and PostHog breaks down on flat keys.
        phoneme: record.phoneme?.code ?? null,
        phoneme_kind: record.phoneme?.kind ?? null,
        word: record.word,
        voice_id: record.voiceId,
        delta_ms: record.deltaMs,
        is_note_start_handle: record.isNoteStartHandle,
      };
    case "ttv_undo":
      return {
        ...common,
        undone_action_type: record.undoneActionType,
        undo_stack_depth: record.undoStackDepth,
        hop_count: record.hopCount,
      };
    case "ttv_redo":
      return {
        ...common,
        redone_action_type: record.redoneActionType,
        redo_stack_depth: record.redoStackDepth,
        hop_count: record.hopCount,
      };
  }
  // The mapper has no declared return type (each case returns its own key set), so an unhandled variant would silently widen it to include undefined and ship an event with no properties. This turns that into a compile error at the one place that must change.
  return assertNever(record);
};
